import { LoadingOutlined } from '@ant-design/icons';
import { useGooglePlacesApi } from 'hooks/use-google-places-api';
import { observer } from 'mobx-react';
import { useEffect, useRef, useState } from 'react';
import { Input, Spin } from 'top-component-library';
import { Container, Loader, PredictionsButton, PredictionsList } from './styles';
import { AddressInputProps } from './types';
import { handlePredictionsDetails, handleQueryChange } from './utils';

const AddressInput = ({
  allowClear,
  className,
  id,
  name,
  onAddressObjectChange,
  onFocusOut,
  onQueryChange,
  placeholder,
  testId,
  initialValue = '',
}: AddressInputProps) => {
  const [debounceHandler, setDebounceHandler] = useState(null);
  const [query, setQuery] = useState(initialValue);
  const [focused, setFocused] = useState(false);
  const [loadingPredictions, setLoadingPredictions] = useState(false);
  const [predictions, setPredictions] = useState(null);
  const wrapperRef = useRef(null);
  const { getAutoCompletePredictions, getPlaceDetails } = useGooglePlacesApi();

  useEffect(() => {
    setQuery(initialValue);
  }, [initialValue]);

  // Detect clicking outside of the input or the list of predictions
  useEffect(() => {
    function handleFocusOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setFocused(false);
        if (onFocusOut) {
          onFocusOut(event);
        }
      }
    }

    // Detect if the user clicks or tabs out of the component
    document.addEventListener('mousedown', handleFocusOutside);
    document.addEventListener('keyup', handleFocusOutside);

    return () => {
      document.removeEventListener('mousedown', handleFocusOutside);
      document.removeEventListener('keyup', handleFocusOutside);
    };
  }, [wrapperRef, onFocusOut]);

  const showPredictionsContainer = loadingPredictions || (focused && query.length > 0);

  return (
    <Container className="address-search__container" ref={wrapperRef}>
      <Input
        name={name}
        id={id ?? name}
        data-testid={testId}
        allowClear={allowClear}
        autoComplete="none"
        className={`${className} address-search__input`}
        showpredictions={showPredictionsContainer ? 1 : 0}
        onChange={(event) =>
          handleQueryChange({
            debounceHandler,
            event,
            getAutoCompletePredictions,
            onQueryChange,
            setDebounceHandler,
            setLoadingPredictions,
            setPredictions,
            setQuery,
          })
        }
        onFocus={() => setFocused(true)}
        placeholder={placeholder}
        value={query}
      />
      {showPredictionsContainer && (
        <PredictionsList data-testid="address-search__predictions-list" className="address-search__predictions-list">
          {!loadingPredictions &&
            predictions?.length &&
            predictions.map((prediction, index) => (
              <PredictionsButton
                key={`predictions-index-${index}`}
                onClick={async () =>
                  await handlePredictionsDetails({
                    getPlaceDetails,
                    onAddressObjectChange,
                    onQueryChange,
                    prediction,
                    setFocused,
                    setPredictions,
                    setQuery,
                  })
                }
              >
                {prediction.description}
              </PredictionsButton>
            ))}
          {loadingPredictions && (
            <Loader className="address-search__loader">
              <Spin tip="Searching Addresses..." indicator={<LoadingOutlined spin />} />
            </Loader>
          )}
        </PredictionsList>
      )}
    </Container>
  );
};

export default observer(AddressInput);
